import { useState, useEffect } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';

import { endpoints } from '../endpoints';
import { setConfig, setConfigLoaded } from '../../store/configSlice';
import { inferTenantIdFromHostname } from '../../analytics/track';
import { applyDocumentTitleForBrand, applyDocumentTitleFromHostname, applyRuntimeTheme, bootstrapThemeFromHostname, getCurrentTheme, inferThemeKeyFromHostname, resolveThemeKeyFromConfig, Theme } from '../../theme';


interface PublicTheme {
  themeKey: string;
  brandName: string;
  branding?: Partial<Theme>;
  brandingAssetVersion?: number;
}

interface Config {
  envName: string;
  isCalendly: boolean;
  isGroupChat: boolean;
  isSurveyForm: boolean;
  useNycIntake: boolean;
  autoGoogleMeet?: boolean;
  isLinkedinLogin?: boolean;
  qrSignupEnabled?: boolean;
  qrProfileEnabled?: boolean;
  qrBookingMentorAsBooker?: boolean;
  aiMatchingPreview?: boolean;
  eventIntakeEnabled?: boolean;
  sendbirdAppId?: string;
  tenantId?: string;
  theme?: PublicTheme;
}

export const useFetchConfig = () => {
  const [isConfigLoading, setIsConfigLoading] = useState<boolean>(true);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get<Config>(endpoints.config);
        const {
          envName,
          isCalendly,
          isGroupChat,
          isSurveyForm,
          useNycIntake,
          autoGoogleMeet,
          isLinkedinLogin,
          qrSignupEnabled,
          qrProfileEnabled,
          qrBookingMentorAsBooker,
          aiMatchingPreview,
          eventIntakeEnabled,
          sendbirdAppId,
          tenantId,
          theme,
        } = response.data;

        let runtimeTheme: Theme | null = null;
        const inferredHostTheme = inferThemeKeyFromHostname();
        const resolvedThemeKey = theme?.themeKey
          ? resolveThemeKeyFromConfig(theme.themeKey)
          : inferredHostTheme || undefined;

        if (resolvedThemeKey) {
          applyRuntimeTheme(
            resolvedThemeKey,
            resolvedThemeKey === theme?.themeKey ? theme?.branding : undefined,
            resolvedThemeKey === theme?.themeKey ? theme?.brandingAssetVersion : undefined
          );
          runtimeTheme = getCurrentTheme();
        } else {
          bootstrapThemeFromHostname();
          runtimeTheme = getCurrentTheme();
        }
        applyDocumentTitleForBrand(theme?.brandName);

        dispatch(setConfig({
          envName, 
          isCalendlyLinkRequired: isCalendly, 
          isAutoGoogleMeetEnabled: !!autoGoogleMeet,
          isGroupChatsEnabled: isGroupChat, 
          isSurveyForm,
          useNycIntake,
          isLinkedinLogin: Boolean(isLinkedinLogin),
          qrSignupEnabled: Boolean(qrSignupEnabled),
          qrProfileEnabled: Boolean(qrProfileEnabled),
          qrBookingMentorAsBooker: Boolean(qrBookingMentorAsBooker),
          aiMatchingPreview: Boolean(aiMatchingPreview),
          eventIntakeEnabled: Boolean(eventIntakeEnabled),
          sendbirdAppId:
            sendbirdAppId ||
            (!inferThemeKeyFromHostname() ? process.env.REACT_APP_SENDBIRD_APP_ID : '') ||
            '',
          tenantId: tenantId || inferTenantIdFromHostname(),
          themeKey: resolvedThemeKey || theme?.themeKey || '',
          brandName: theme?.brandName || '',
          brandingAssetVersion: theme?.brandingAssetVersion || 0,
          runtimeTheme,
        }));
      } catch (error) {
        console.error("Failed to load config:", error);
        bootstrapThemeFromHostname();
        applyDocumentTitleFromHostname();
        dispatch(setConfigLoaded(true));
      } finally {
        setIsConfigLoading(false);
      }
    };

    fetchConfig();
  }, [dispatch]);

  return { isConfigLoading };
};
